import type Swiper from 'swiper';
import { default as tgsap } from 'gsap';
import type {
    AnimFn,
    ApplicationAssetKey,
    ApplicationAssetValue,
    ApplicationAssets,
    ApplicationAssetsMap,
    ApplicationBase,
    CleanupFn,
    OutroFn,
    ThemeData,
    TransitionType,
} from '../../../../@types';
import { ScrollTrigger as TScrollTrigger } from 'gsap/ScrollTrigger';

class Application implements ApplicationBase {
    animFunctions: AnimFn[];
    outroFns: OutroFn[];
    cleanupFns: CleanupFn[];
    swiperInstances: { [key: string]: Swiper };
    gsapRegistered: { gsap: typeof gsap; ScrollTrigger: typeof ScrollTrigger } | null;
    themeData: ThemeData;
    assets: ApplicationAssets;

    constructor() {
        this.animFunctions = [];
        this.outroFns = [];
        this.cleanupFns = [];
        this.swiperInstances = {};
        this.gsapRegistered = null;
        this.assets = new Map();
        this.themeData = {
            sectors: {
                commercial: '',
                weddings: '',
                aerial: '',
                automotive: '',
                landscape: '',
                astrophotography: '',
                'videos-and-reels': '',
            },
            content: [],
        };

        this.hydrateThemeData();
    }

    addAnimFunction(fn: AnimFn) {
        if (!Array.isArray(fn)) {
            this.animFunctions.push(fn);
        } else {
            this.animFunctions.push(...fn);
        }
    }

    runAnimFunctions() {
        this.animFunctions.forEach((fn) => fn(this));
    }

    addOutroFn(fn: OutroFn | OutroFn[]) {
        if (Array.isArray(fn)) {
            this.outroFns.push(...fn);
        } else {
            this.outroFns.push(fn);
        }
    }

    runOutroFns(tl: gsap.core.Timeline, duration: number, transitionType: TransitionType) {
        this.outroFns.flat().forEach((fn) => fn(tl, duration, transitionType));
    }

    addCleanupFn(fn: CleanupFn | CleanupFn[]) {
        if (Array.isArray(fn)) {
            this.cleanupFns.push(...fn);
        } else {
            this.cleanupFns.push(fn);
        }
    }

    runCleanupFns(transitionType: TransitionType) {
        this.cleanupFns.flat().forEach((fn) => fn(transitionType));
        this.cleanupFns = [];
        this.outroFns = [];
    }

    addSwiperInstance(instance: Swiper, name?: string) {
        if (!name) {
            name = String(Math.random());
        }
        this.swiperInstances[name] = instance;
    }

    cleanupSwiperInstance() {
        for (const swiper in this.swiperInstances) {
            this.swiperInstances[swiper].destroy(true, true);
            delete this.swiperInstances[swiper];
        }
    }

    registerGSAP(gsap: typeof tgsap, ScrollTrigger: typeof TScrollTrigger) {
        this.gsapRegistered = { gsap, ScrollTrigger };
    }

    addAsset<K extends ApplicationAssetKey>(key: K, asset: ApplicationAssetValue<K>) {
        this.assets.set(key, asset);
    }

    getAsset<K extends ApplicationAssetKey>(key: K): ApplicationAssetValue<K> | undefined;
    getAsset<
        const K extends readonly [ApplicationAssetKey, ApplicationAssetKey, ...ApplicationAssetKey[]],
    >(
        ...keys: K
    ): ApplicationAssetsMap<K>;
    getAsset(
        ...keys: [ApplicationAssetKey, ...ApplicationAssetKey[]]
    ): unknown | undefined | ApplicationAssetsMap<readonly ApplicationAssetKey[]> {
        if (keys.length === 1) {
            return this.assets.get(keys[0]);
        }

        const assets = keys.map((key) => [key, this.assets.get(key)] as const);
        return Object.fromEntries(assets) as ApplicationAssetsMap<typeof keys>;
    }

    removeAsset(key: ApplicationAssetKey) {
        this.assets.delete(key);
    }

    hydrateThemeData(textContent?: string) {
        if (!textContent) {
            const el = document.querySelector('script[data-theme-data]');

            if (!el?.textContent) {
                return;
            }
            textContent = el.textContent;
        }

        try {
            this.themeData = JSON.parse(textContent);
        } catch {}
    }
}

let applicationInstance: Application | null = null;

export function initApplicationInstance() {
    if (!applicationInstance) {
        applicationInstance = new Application();
    }
    return applicationInstance;
}

export function getApplicationInstance() {
    if (!applicationInstance) throw new Error('Application not initialized!');
    return applicationInstance;
}
